import React, { useReducer, useRef, useState } from "react"
import { Formik, FormikHelpers, Field, ErrorMessage } from "formik"
import tw, { styled } from "twin.macro"
import { P, H3 } from "../components/page-elements"
import { SectionTitle } from "../components/section-title"
import * as yup from "yup"

const Container = tw.div`
    bg-white
    max-w-lg
    shadow-lg
    rounded-lg
    m-auto
    p-8
    w-full
`

const Form = tw.form`
    flex
    flex-col
    justify-center
    my-10
`

const StyledInput = styled(Field)`
  ${tw`
    text-xl
    text-header
    placeholder-gray-500
    border
    border-gray-500
    rounded
    mt-6
    py-4
    px-4
    focus:outline-none
    focus:border-primary
    w-full
    `}
`

const StyledTextarea = styled(Field)`
  ${tw`
    text-xl
    text-header
    placeholder-gray-500
    border
    border-gray-500
    rounded
    mt-6
    py-4
    px-4
    focus:outline-none
    focus:border-primary
    h-32
    w-full
    `}
`

const Button = tw.button`
    bg-primary
    inline-block
    rounded-sm 
    px-4 
    my-4
    md:px-5 
    xl:px-4 
    py-3 
    md:py-4 
    xl:py-3 
    hover:bg-primary
    md:text-lg 
    xl:text-base-color 
    text-base-color 
    leading-tight 
    shadow-md
    uppercase
`

const SuccessMessage = tw.div`
  text-center
`

const StyleErrorMessage = styled(ErrorMessage as any)`
  ${tw`
    text-red-500
    block
    text-sm
    font-bold
    my-0
    mx-2
    text-left
  `}
`

const StyledFormError = tw.p`
    text-red-500
    block
    text-sm
    font-bold
    my-0
    mx-2
    text-left
`

const phoneRegExp = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/

let Schema = yup.object().shape({
  name: yup.string().required().min(2, "Too Short!").max(50, "Too Long!"),
  business: yup.string().max(50, "Too Long!"),
  email: yup
    .string()
    .email()
    .required()
    .min(2, "Too Short!")
    .max(50, "Too Long!"),
  phone: yup
    .string()
    .matches(
      phoneRegExp,
      "Phone number is not valid. Must be standard 10 digit US number."
    )
    .max(50, "Too Long!"),
  message: yup.string().required().min(2, "Too Short!").max(2000, "Too Long!"),
})

interface ContactForm {
  name: string
  email: string
  phone: string
  business: string
  message: string
  [key: string]: string
}

const encode = (data: ContactForm) => {
  return Object.keys(data)
    .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&")
}

const STATUS_PENDING = "STATUS_PENDING"
const STATUS_SUCCESS = "STATUS_SUCCESS"
const STATUS_FAILURE = "STATUS_FAILURE"

const initialValues = {
  name: "",
  email: "",
  phone: "",
  business: "",
  message: "",
}

export const ContactForm = () => {
  const onSubmit = (
    values: ContactForm,
    { setSubmitting, resetForm, setStatus }: FormikHelpers<ContactForm>
  ) => {
    setSubmitting(true)
    setStatus(STATUS_PENDING)

    fetch("/?no-cache=1", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": "contact",
        ...values,
      }),
    })
      .then(() => {
        setStatus(STATUS_SUCCESS)
        setSubmitting(false)
      })
      .catch(error => {
        setStatus(STATUS_FAILURE)
        setSubmitting(false)
      })
  }

  return (
    <Container>
      <Formik
        initialValues={initialValues}
        validationSchema={Schema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, status, handleSubmit }) =>
          status === STATUS_SUCCESS ? (
            <SuccessMessage>
              <H3 className="text-header">Thanks for reaching out!</H3>
              <P className="text-body">
                We'll get back to as soon as possible!
              </P>
            </SuccessMessage>
          ) : (
            <Form
              name="contact"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={e => {
                e.preventDefault()
                handleSubmit()
              }}
            >
              <SectionTitle text="Contact Us" />
              <input type="hidden" name="form-name" value="contact" />
              <input type="hidden" name="bot-field" />
              <StyledInput placeholder="Name" name="name" />
              <StyleErrorMessage name="name" component="p" />
              <StyledInput placeholder="Business" name="business" />
              <StyleErrorMessage name="business" component="p" />
              <StyledInput
                placeholder="Email Address"
                name="email"
                type="email"
              />
              <StyleErrorMessage name="email" component="p" />
              <StyledInput placeholder="Phone Number" name="phone" />
              <StyleErrorMessage name="phone" component="p" />
              <StyledTextarea
                type="textarea"
                component="textarea"
                placeholder="Message"
                name="message"
              />
              <StyleErrorMessage name="message" component="p" />
              {status !== undefined && status === STATUS_FAILURE && (
                <StyledFormError>
                  Oops! Something went wrong. Please try again.
                </StyledFormError>
              )}
              <Button type="submit" disabled={isSubmitting}>
                Submit
              </Button>
            </Form>
          )
        }
      </Formik>
    </Container>
  )
}
